import { get_balances } from "@/api/v4/balance";
import { getFiatListByBalance } from "@/api/v4/fiat";
import { normalizeAicopyBalanceRaw } from "@/components/Pages/aicopy/utils/aicopyCurrencies";
import { isFiatMarketCode } from "@/utils/fiatMarket";

function readCryptoBalanceEntry(result, currencyCode) {
  const upper = String(currencyCode || "").toUpperCase();
  if (!upper) return { amount: "0", convertUsdtAmount: null };
  let asset;
  if (result?.assets && Array.isArray(result.assets)) {
    asset = result.assets.find((a) => String(a.currency || "").toUpperCase() === upper);
  } else if (result && typeof result === "object" && result[upper]) {
    asset = result[upper];
  } else if (result?.totalUsdtAmount != null && upper === "USDT") {
    return {
      amount: String(result.totalUsdtAmount),
      convertUsdtAmount: String(result.totalUsdtAmount),
    };
  }
  if (asset == null) return { amount: "0", convertUsdtAmount: null };
  return {
    amount: String(asset.totalAmount ?? asset.availableAmount ?? asset.available ?? "0"),
    convertUsdtAmount:
      asset.convertUsdtAmount != null ? String(asset.convertUsdtAmount) : null,
  };
}

function readBalanceAmount(entry) {
  if (entry == null) return "0";
  if (typeof entry === "object") return entry.amount ?? "0";
  return entry;
}

/** 与 wallet overview 一致：法币 2 位小数，加密货币去尾零 */
export function formatConvertBalanceDisplay(raw, isFiat) {
  const normalized = normalizeAicopyBalanceRaw(readBalanceAmount(raw));
  const n = parseFloat(normalized);
  if (!Number.isFinite(n)) return "0";
  if (isFiat) {
    const s = n.toFixed(2).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
    return s || "0";
  }
  if (Number.isInteger(n)) return String(n);
  const s = n.toFixed(8).replace(/(\.\d*?[1-9])0+$/, "$1").replace(/\.0+$/, "");
  return s || "0";
}

/** 第二行折算展示：≈R$123.45（跟随 fund.assist，与 wallet overview 一致） */
export function formatConvertBalanceApproxDisplay(entry, code, fund) {
  const amountNum = parseFloat(normalizeAicopyBalanceRaw(readBalanceAmount(entry)));
  if (!Number.isFinite(amountNum) || Math.abs(amountNum) <= 0) return null;

  const coinLower = String(code || "").toLowerCase();
  let converted = fund?.getCoinValue?.(amountNum, coinLower, undefined, true);

  const convertUsdt =
    typeof entry === "object" && entry?.convertUsdtAmount != null
      ? entry.convertUsdtAmount
      : null;
  if (
    convertUsdt != null &&
    (!converted || converted === "0" || converted === "--")
  ) {
    converted = fund?.getCoinValue?.(convertUsdt, "usdt", undefined, true);
  }

  if (!converted || converted === "--" || converted === "0") return null;

  const note = fund?.assist?.note ?? "$";
  return `≈${note}${converted}`;
}

/** 拉取 From 列表各币种余额（crypto: totalAmount；fiat: totalAmount ?? availableAmount） */
export async function fetchConvertFromBalanceMap(fromOptions = []) {
  const fiatCodes = [];
  const cryptoCodes = [];
  const map = {};

  (fromOptions || []).forEach((opt) => {
    const code = String(opt?.code || "").toUpperCase();
    if (!code) return;
    map[code] = { amount: "0", convertUsdtAmount: null };
    const isFiat = opt.isFiat ?? isFiatMarketCode(code);
    if (isFiat) fiatCodes.push(code);
    else cryptoCodes.push(code);
  });

  const tasks = [];

  if (cryptoCodes.length) {
    tasks.push(
      get_balances({
        params: { currencies: cryptoCodes.join(",") },
        isResFull: true,
      })
        .then((res) => {
          const result = res?.result;
          cryptoCodes.forEach((code) => {
            const entry = readCryptoBalanceEntry(result, code);
            map[code] = {
              amount: normalizeAicopyBalanceRaw(entry.amount),
              convertUsdtAmount:
                entry.convertUsdtAmount != null
                  ? normalizeAicopyBalanceRaw(entry.convertUsdtAmount)
                  : null,
            };
          });
        })
        .catch((e) => {
          console.error("convert fetch crypto balances", e);
        })
    );
  }

  if (fiatCodes.length) {
    tasks.push(
      getFiatListByBalance({
        params: { pageIndex: 1, pageSize: 100 },
        isAutoPopError: false,
      })
        .then((res) => {
          const balanceData = res?.result ?? res?.data ?? res;
          const items = Array.isArray(balanceData)
            ? balanceData
            : balanceData?.items ?? balanceData?.list ?? [];
          fiatCodes.forEach((code) => {
            const matched = items.find(
              (item) => String(item.coinName ?? item.currency ?? "").toUpperCase() === code
            );
            map[code] = matched
              ? {
                  amount: normalizeAicopyBalanceRaw(
                    matched.totalAmount ?? matched.availableAmount ?? 0
                  ),
                  convertUsdtAmount:
                    matched.convertUsdtAmount != null
                      ? normalizeAicopyBalanceRaw(matched.convertUsdtAmount)
                      : null,
                }
              : { amount: "0", convertUsdtAmount: null };
          });
        })
        .catch((e) => {
          console.error("convert fetch fiat balances", e);
        })
    );
  }

  await Promise.all(tasks);
  return map;
}
